import * as microsoftTeams from "@microsoft/teams-js";
import * as React from "react";
import Spinner, { SpinnerSize } from "sh-application/components/common/Spinner";
import StringsStore from "sh-strings/store";
import TeamSetupPickerUtils from "../TeamSetupPickerUtils";
import { fireAccessibilityAlert } from "sh-application/components/accessibilityAlert";
import { IButton, Image, PrimaryButton } from "@fluentui/react";
import { MSTeamPickerPaneStore } from "./store/schema/MSTeamPickerPaneStore";
import { observer } from "mobx-react";
import { setShowStepIndicator } from "../store/store";
import { ECSConfigKey, ECSConfigService } from "sh-services";
import { IMsTeamWithScheduleInfo } from "sh-models";
import { TeamSetupMode } from "../store/schema/TeamSetupPickerStore";

const styles = require("./MSTeamPickerPane.scss");
const emptyMessageImage = require("sh-assets/images/optimized-svg/Shifts_EmptyState_desktop_NoSchedules.svg");

export interface MSTeamPickerPaneProps {
    instrumentationEventName: string;
    onTeamSelected: (teamWithScheduleInfo: IMsTeamWithScheduleInfo, setupMode: TeamSetupMode) => void;
    store: MSTeamPickerPaneStore;
    loadInitialData: () => void;
    autofocus?: boolean;
}

/**
 * React component that renders the MS team picker UX.
 * In this step, the user chooses an MS Team to create a schedule team for.
 */
@observer
export default class MSTeamPickerPane extends React.Component<MSTeamPickerPaneProps, any> {
    private _strings: Map<string, string>;

    private _firstSelectableItemRef = React.createRef<IButton>();  // ref for the first selectable team item. used for setting initial focus.

    constructor(props: MSTeamPickerPaneProps) {
        super(props);

        this._strings = StringsStore().registeredStringModules.get("msTeamPicker").strings;
    }

    async componentDidMount() {
        await this.props.loadInitialData();

        // Fire accessibility alert because the heading content doesn't get read via focus.
        // The team setup dialog is reused between steps and only its inner content is changed
        fireAccessibilityAlert(this._strings.get("msTeamPickerDialogAriaAlert"));

        if (this.props.autofocus && this._firstSelectableItemRef && this._firstSelectableItemRef.current) {
            // Set initial focus on the first selectable team item
            this._firstSelectableItemRef.current.focus();
        }
    }

    /**
     * Returns true if the teams data is currently being fetched
     */
    private isTeamsDataLoading(): boolean {
        return this.props.store.isDataLoading;
    }

    /**
     * Renders loading progress state
     */
    private renderLoadingProgress(): JSX.Element {
        return (
            <div className={ styles.progressIndicatorContainer }>
                <Spinner className={ styles.spinner } size={ SpinnerSize.small } label={ this._strings.get("loadingTeamsMessage") } />
            </div>
        );
    }

    /**
     * Render empty state when no teams are available to be listed
     */
    private renderEmptyTeamListState(): JSX.Element {
        // Display empty state message
        return (
            <div className={ styles.emptyStateContainer } aria-live="polite">
                <Image alt="" className={ styles.messageImage } src={ emptyMessageImage } />
                <div className={ styles.title }>{ this._strings.get("noTeamsToCreateScheduleTitle") }</div>
                <div className={ styles.subtitle }>{ this._strings.get("noTeamsToCreateScheduleSubtitle") }</div>
            </div>
        );
    }

    /**
     * Handler for when a create/restore team button is clicked
     */
    private handleSelectTeamButtonClicked(teamWithScheduleInfo: IMsTeamWithScheduleInfo, setupMode: TeamSetupMode) {
        if (this.props.onTeamSelected) {
            this.props.onTeamSelected(teamWithScheduleInfo, setupMode);
        }
    }

    /**
     * Render the MS teams picker list contents
     */
    private renderMSTeamsPickerListContents(): JSX.Element {
        const msTeamsWithoutActiveSchedules = this.props.store.msTeamsWithoutActiveSchedules;
        let hasSchedulesToCreate = false;
        const hasTeams = msTeamsWithoutActiveSchedules && msTeamsWithoutActiveSchedules.length > 0;
        if (hasTeams) {
            for (let i = 0; i < msTeamsWithoutActiveSchedules.length; i++) {
                // the user must be admin for at least 1 team to create a schedule
                if (TeamSetupPickerUtils.isUserTeamAdmin(msTeamsWithoutActiveSchedules[i].msTeamInfo)) {
                    hasSchedulesToCreate = true;
                    // show the step indicator
                    setShowStepIndicator(true);
                    break;
                }
            }
        }

        return (
            this.isTeamsDataLoading()
            ?
            this.renderLoadingProgress()
            :
            (
                hasTeams && hasSchedulesToCreate
                ?
                this.renderMSTeamsPickerList(msTeamsWithoutActiveSchedules)
                :
                this.renderEmptyTeamListState()
            )
        );
    }

    /**
     * Render the MS teams picker list UX
     */
    private renderMSTeamsPickerList(msTeamsWithScheduleInfo: IMsTeamWithScheduleInfo[]): JSX.Element {
        return (
            <div className={ styles.pickerList }>
                <div className={ styles.header }>
                    <h1 className={ styles.headerTitle }>{ this._strings.get("msTeamPickerTitle") }</h1>
                    <div className={ styles.headerSubtitle }>{ this._strings.get("msTeamPickerDescription") }</div>
                </div>
                <div className={ styles.itemList } >
                    { this.renderMSTeamsListItems(msTeamsWithScheduleInfo) }
                </div>
            </div>
        );
    }

    /**
     * Render the list items for the schedule teams list
     */
    private renderMSTeamsListItems(msTeamsWithScheduleInfo: IMsTeamWithScheduleInfo[]): JSX.Element {
        let teamInfoRenderItems: JSX.Element[] = [];
        if (msTeamsWithScheduleInfo) {
            let foundFirstSelectableItem: boolean = false;
            let filteredTeams = msTeamsWithScheduleInfo;
            if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.SeparateArchiveTeamsViewEnabled)) {
                filteredTeams = msTeamsWithScheduleInfo.filter((teamWithSchedule) => teamWithSchedule.msTeamInfo.isTeamArchived === false);
            }
            for (let i = 0; i < filteredTeams.length; i++) {
                if (filteredTeams[i]) {
                    const isDisabled: boolean = !TeamSetupPickerUtils.isUserTeamAdmin(filteredTeams[i].msTeamInfo);
                    let isFirstSelectableItem: boolean = false;
                    if (!isDisabled && !foundFirstSelectableItem) {
                        isFirstSelectableItem = true;
                        foundFirstSelectableItem = true;
                    }
                    teamInfoRenderItems.push(this.renderMSTeamListItem(filteredTeams[i], i.toString(), isDisabled, isFirstSelectableItem));
                }
            }
        }

        // TODO (DCoh): Investigate whether we can use a List component instead of creating a list from scratch,
        // which may give us more consistent support for things like accessibility, focus handling, etc.
        return (
            <ul className={ styles.teamList }>
                { teamInfoRenderItems }
            </ul>
        );
    }

    /**
     * Render the left side of a list item for MS teams
     */
    private renderMSTeamListItemLeft(teamInfo: microsoftTeams.TeamInformation): JSX.Element {
        return (
            <span className={ styles.listItemName }>{ teamInfo.teamName }</span>
        );
    }

    /**
     * Calculate the aria label for the create team schedule button
     */
    private getCreateButtonAriaLabel(teamInfo: microsoftTeams.TeamInformation): string {
        return this._strings.get("createTeamScheduleButtonAriaLabel").format(teamInfo.teamName);
    }

    /**
     * Calculate the aria label for the restore team schedule button
     */
    private getRestoreButtonAriaLabel(teamInfo: microsoftTeams.TeamInformation): string {
        return this._strings.get("restoreTeamScheduleButtonAriaLabel").format(teamInfo.teamName);
    }

    /**
     * Render a list item for the MS teams list
     * @param teamWithScheduleInfo
     * @param reactItemKey key for react list item rendering
     * @param isDisabled true if this item is disabled
     * @param isFirstSelectableItem true if this is the first item in the list
     */
    private renderMSTeamListItem(teamWithScheduleInfo: IMsTeamWithScheduleInfo, reactItemKey: string, isDisabled: boolean, isFirstSelectableItem: boolean): JSX.Element {
        const teamInfo = teamWithScheduleInfo.msTeamInfo;
        const scheduleInfo = teamWithScheduleInfo.scheduleTeamInfo;

        // Don't render if this team already has an active (not deleted) schedule
        const hasActiveSchedule = scheduleInfo && !scheduleInfo.isSoftDeleted;
        if (hasActiveSchedule) {
            // should not come here because we expect the list of teams to be pre-filtered
            return null;
        }

        // Check if this team has a soft-deleted (restorable) schedule
        const isRestoreScheduleEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.RestoreScheduleEnabled);
        const hasRestorableSchedule = isRestoreScheduleEnabled && scheduleInfo && scheduleInfo.isSoftDeleted === true;

        // Determine the action based on whether we're restoring or creating
        const setupMode = hasRestorableSchedule ? TeamSetupMode.RestoreOriginalSchedule : TeamSetupMode.CreateNewSchedule;

        const handleCreateRestoreButtonClicked = () => { this.handleSelectTeamButtonClicked(teamWithScheduleInfo, setupMode); };
        const btnDescription: string = isDisabled ?
            this._strings.get("disabledScheduleButtonTooltip") :
            (hasRestorableSchedule ?
                this.getRestoreButtonAriaLabel(teamInfo) :
                this.getCreateButtonAriaLabel(teamInfo)); // Display a tooltip explaining why the button is disabled
        const buttonClassName: string = isDisabled ? styles.disabledMSTeamButton : "msTeamSelectButton";

        // Use different button text based on whether we're creating or restoring
        const buttonText = hasRestorableSchedule ? this._strings.get("restoreScheduleButtonLabel") : this._strings.get("selectTeamButtonLabel");

        return (
            <li className={ styles.teamListItem } key={ reactItemKey }>
                <div className={ styles.left } >
                    {
                        this.renderMSTeamListItemLeft(teamInfo)
                    }
                </div>
                <div className={ styles.right } >
                    <PrimaryButton
                        componentRef={ isFirstSelectableItem ? this._firstSelectableItemRef : null }
                        disabled={ isDisabled }
                        allowDisabledFocus={!isDisabled}
                        data-automation-id={"createButton_" + teamInfo.teamName}
                        className={ buttonClassName }
                        onClick={ handleCreateRestoreButtonClicked }
                        ariaDescription={ btnDescription } >
                        { buttonText }
                    </PrimaryButton>
                </div>
            </li>
        );
    }

    render() {
        return (
            <div className={ styles.content }>
                {
                    this.renderMSTeamsPickerListContents()
                }
            </div>
        );
    }
}